/**
 * SettingsPanel Component
 *
 * In-app admin panel with two tabs:
 *  - Models: manage AI model configurations (Azure Table Storage via /api/models)
 *  - Agents: manage Foundry agent configs (Azure Table Storage via /api/foundry-agent-config)
 * Accessible via the Settings (gear) button in the Header.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  tokens,
  makeStyles,
  Button,
  Text,
  Input,
  Label,
  Spinner,
  Badge,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Tooltip,
  Switch,
  Dropdown,
  Option,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Tab,
  TabList,
} from '@fluentui/react-components';
import {
  DismissRegular,
  AddRegular,
  EditRegular,
  DeleteRegular,
  SaveRegular,
} from '@fluentui/react-icons';
import { ModelConfig } from '../types';

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 2000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '80px',
    overflowY: 'auto',
  },
  panel: {
    width: '720px',
    maxWidth: '95vw',
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow64,
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  panelBody: {
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    overflowY: 'auto',
    flexGrow: 1,
  },
  sectionTitle: {
    marginBottom: tokens.spacingVerticalM,
  },
  modelList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  modelCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
  },
  modelInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flexGrow: 1,
    gap: '2px',
  },
  modelName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  modelMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
  },
  modelActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingVerticalM,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  formFull: {
    gridColumn: '1 / -1',
  },
  emptyState: {
    textAlign: 'center',
    padding: `${tokens.spacingVerticalXXL} 0`,
    color: tokens.colorNeutralForeground3,
  },
  toast: {
    marginBottom: tokens.spacingVerticalM,
  },
});

interface ModelFormData {
  name: string;
  provider: string;
  model: string;
  endpoint: string;
  apiKey: string;
  description: string;
  capabilities: string;
  costPer1kInputTokens: string;
  costPer1kOutputTokens: string;
}

interface AgentConfig {
  id: string;
  name: string;
  description: string;
  foundryAgentId: string;
  foundryEndpoint: string;
  hasApiKey: boolean;
  enabled: boolean;
}

interface AgentFormData {
  name: string;
  description: string;
  foundryAgentId: string;
  foundryEndpoint: string;
  apiKey: string;
}

const EMPTY_AGENT_FORM: AgentFormData = {
  name: '',
  description: '',
  foundryAgentId: '',
  foundryEndpoint: '',
  apiKey: '',
};

const EMPTY_FORM: ModelFormData = {
  name: '',
  provider: 'azure-openai',
  model: '',
  endpoint: '',
  apiKey: '',
  description: '',
  capabilities: 'chat',
  costPer1kInputTokens: '',
  costPer1kOutputTokens: '',
};

const PROVIDERS = [
  { value: 'azure-openai', label: 'Azure OpenAI' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'custom', label: 'Custom' },
];

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<string>('models');
  const [models, setModels] = useState<(ModelConfig & { endpoint?: string })[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; intent: 'success' | 'error' } | null>(null);

  // Model form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ModelFormData>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Agent state
  const [agentConfigs, setAgentConfigs] = useState<AgentConfig[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isAgentFormOpen, setIsAgentFormOpen] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [agentForm, setAgentForm] = useState<AgentFormData>(EMPTY_AGENT_FORM);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [deleteAgentTarget, setDeleteAgentTarget] = useState<{ id: string; name: string } | null>(null);

  const showToast = useCallback((message: string, intent: 'success' | 'error') => {
    setToast({ message, intent });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/models');
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setModels(data.models || []);
    } catch (err: any) {
      showToast(`Failed to load models: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  // ── Agent CRUD helpers ──────────────────────────────────────
  const loadAgentConfigs = useCallback(async () => {
    setIsLoadingAgents(true);
    try {
      const res = await fetch('/api/foundry-agent-config');
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setAgentConfigs(data.agents || []);
    } catch (err: any) {
      showToast(`Failed to load agents: ${err.message}`, 'error');
    } finally {
      setIsLoadingAgents(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isOpen) {
      loadModels();
      loadAgentConfigs();
    }
  }, [isOpen, loadModels, loadAgentConfigs]);

  const openAddAgentForm = () => {
    setAgentForm(EMPTY_AGENT_FORM);
    setEditingAgentId(null);
    setIsAgentFormOpen(true);
  };

  const openEditAgentForm = async (id: string) => {
    try {
      const res = await fetch(`/api/foundry-agent-config/${id}`);
      if (!res.ok) throw new Error(res.statusText);
      const a = await res.json();
      setAgentForm({
        name: a.name || '',
        description: a.description || '',
        foundryAgentId: a.foundryAgentId || '',
        foundryEndpoint: a.foundryEndpoint || '',
        apiKey: '', // never pre-fill
      });
      setEditingAgentId(id);
      setIsAgentFormOpen(true);
    } catch (err: any) {
      showToast(`Failed to load agent: ${err.message}`, 'error');
    }
  };

  const handleSaveAgent = async () => {
    if (!agentForm.name || !agentForm.foundryAgentId || !agentForm.foundryEndpoint || (!editingAgentId && !agentForm.apiKey)) {
      showToast('Fill in all required fields', 'error');
      return;
    }
    setIsSavingAgent(true);
    try {
      const body: any = {
        name: agentForm.name,
        description: agentForm.description,
        foundryAgentId: agentForm.foundryAgentId,
        foundryEndpoint: agentForm.foundryEndpoint,
      };
      if (agentForm.apiKey) body.apiKey = agentForm.apiKey;

      const url = editingAgentId ? `/api/foundry-agent-config/${editingAgentId}` : '/api/foundry-agent-config';
      const method = editingAgentId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(e.error || res.statusText);
      }
      showToast(editingAgentId ? 'Agent updated' : 'Agent created', 'success');
      setIsAgentFormOpen(false);
      setEditingAgentId(null);
      await loadAgentConfigs();
    } catch (err: any) {
      showToast(`Save failed: ${err.message}`, 'error');
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!deleteAgentTarget) return;
    try {
      const res = await fetch(`/api/foundry-agent-config/${deleteAgentTarget.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(res.statusText);
      showToast('Agent deleted', 'success');
      setDeleteAgentTarget(null);
      await loadAgentConfigs();
    } catch (err: any) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  const handleToggleAgent = async (id: string, enabled: boolean) => {
    try {
      const res = await fetch(`/api/foundry-agent-config/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(res.statusText);
      showToast(enabled ? 'Agent enabled' : 'Agent disabled', 'success');
      await loadAgentConfigs();
    } catch (err: any) {
      showToast(`Toggle failed: ${err.message}`, 'error');
    }
  };

  const openAddForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setIsFormOpen(true);
  };

  const openEditForm = async (id: string) => {
    try {
      const res = await fetch(`/api/models/${id}`);
      if (!res.ok) throw new Error(res.statusText);
      const m = await res.json();
      setForm({
        name: m.name || '',
        provider: m.provider || 'azure-openai',
        model: m.model || '',
        endpoint: m.endpoint || '',
        apiKey: '', // Don't prefill API key
        description: m.description || '',
        capabilities: (m.capabilities || []).join(', '),
        costPer1kInputTokens: m.costPer1kInputTokens?.toString() || '',
        costPer1kOutputTokens: m.costPer1kOutputTokens?.toString() || '',
      });
      setEditingId(id);
      setIsFormOpen(true);
    } catch (err: any) {
      showToast(`Failed to load model: ${err.message}`, 'error');
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.provider || !form.model || !form.endpoint || (!editingId && !form.apiKey)) {
      showToast('Fill in all required fields', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const body: any = {
        name: form.name,
        provider: form.provider,
        model: form.model,
        endpoint: form.endpoint,
        description: form.description,
        capabilities: form.capabilities.split(',').map(s => s.trim()).filter(Boolean),
        costPer1kInputTokens: parseFloat(form.costPer1kInputTokens) || 0,
        costPer1kOutputTokens: parseFloat(form.costPer1kOutputTokens) || 0,
      };
      if (form.apiKey) body.apiKey = form.apiKey;

      const url = editingId ? `/api/models/${editingId}` : '/api/models';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(e.error || res.statusText);
      }
      showToast(editingId ? 'Model updated' : 'Model created', 'success');
      setIsFormOpen(false);
      setEditingId(null);
      await loadModels();
    } catch (err: any) {
      showToast(`Save failed: ${err.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/models/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(res.statusText);
      showToast('Model deleted', 'success');
      setDeleteTarget(null);
      await loadModels();
    } catch (err: any) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    try {
      const res = await fetch(`/api/models/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(res.statusText);
      showToast(enabled ? 'Model enabled' : 'Model disabled', 'success');
      await loadModels();
    } catch (err: any) {
      showToast(`Toggle failed: ${err.message}`, 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.panelHeader}>
          <Text size={500} weight="semibold">Settings</Text>
          <Button icon={<DismissRegular />} appearance="subtle" onClick={onClose} aria-label="Close settings" />
        </div>

        {/* Tabs */}
        <div style={{ padding: `0 ${tokens.spacingHorizontalXL}`, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
          <TabList selectedValue={activeTab} onTabSelect={(_, d) => setActiveTab(d.value as string)}>
            <Tab value="models">Models</Tab>
            <Tab value="agents">Foundry Agents</Tab>
          </TabList>
        </div>

        {/* Body */}
        <div className={styles.panelBody}>
          {toast && (
            <MessageBar intent={toast.intent} className={styles.toast}>
              <MessageBarBody>
                <MessageBarTitle>{toast.intent === 'success' ? 'Success' : 'Error'}</MessageBarTitle>
                {toast.message}
              </MessageBarBody>
            </MessageBar>
          )}

          {/* ══════════ Models Tab ══════════ */}
          {activeTab === 'models' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM }}>
                <Text size={400} weight="semibold" className={styles.sectionTitle}>Configured Models</Text>
                <Button icon={<AddRegular />} appearance="primary" size="small" onClick={openAddForm}>
                  Add Model
                </Button>
              </div>

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '24px' }}><Spinner label="Loading models..." /></div>
              ) : models.length === 0 ? (
                <div className={styles.emptyState}>
                  <Text>No models configured yet. Add one to get started.</Text>
                </div>
              ) : (
                <div className={styles.modelList}>
                  {models.map((m) => (
                    <div key={m.id} className={styles.modelCard}>
                      <div className={styles.modelInfo}>
                        <Text className={styles.modelName}>{m.name}</Text>
                        <Text className={styles.modelMeta}>{m.model} — {m.provider}</Text>
                        {m.description && (
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{m.description}</Text>
                        )}
                        {m.capabilities && m.capabilities.length > 0 && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                            {m.capabilities.map((c, i) => (
                              <Badge key={i} appearance="outline" size="small">{c}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className={styles.modelActions}>
                        <Tooltip content={m.enabled ? 'Disable' : 'Enable'} relationship="label">
                          <Switch
                            checked={m.enabled}
                            onChange={(_, data) => handleToggle(m.id, data.checked)}
                          />
                        </Tooltip>
                        <Tooltip content="Edit" relationship="label">
                          <Button icon={<EditRegular />} appearance="subtle" size="small" onClick={() => openEditForm(m.id)} />
                        </Tooltip>
                        <Tooltip content="Delete" relationship="label">
                          <Button icon={<DeleteRegular />} appearance="subtle" size="small" onClick={() => setDeleteTarget({ id: m.id, name: m.name })} />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ══════════ Agents Tab ══════════ */}
          {activeTab === 'agents' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM }}>
                <Text size={400} weight="semibold" className={styles.sectionTitle}>Foundry Agent Configurations</Text>
                <Button icon={<AddRegular />} appearance="primary" size="small" onClick={openAddAgentForm}>
                  Add Agent
                </Button>
              </div>

              {isLoadingAgents ? (
                <div style={{ textAlign: 'center', padding: '24px' }}><Spinner label="Loading agents..." /></div>
              ) : agentConfigs.length === 0 ? (
                <div className={styles.emptyState}>
                  <Text>No Foundry agents configured yet. Add one to connect an Azure AI Foundry assistant.</Text>
                </div>
              ) : (
                <div className={styles.modelList}>
                  {agentConfigs.map((a) => (
                    <div key={a.id} className={styles.modelCard}>
                      <div className={styles.modelInfo}>
                        <Text className={styles.modelName}>{a.name}</Text>
                        <Text className={styles.modelMeta}>{a.foundryAgentId}</Text>
                        {a.description && (
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{a.description}</Text>
                        )}
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {a.foundryEndpoint}
                        </Text>
                        <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                          <Badge appearance={a.hasApiKey ? 'filled' : 'outline'} color={a.hasApiKey ? 'success' : 'warning'} size="small">
                            {a.hasApiKey ? 'API Key set' : 'No API Key'}
                          </Badge>
                        </div>
                      </div>
                      <div className={styles.modelActions}>
                        <Tooltip content={a.enabled ? 'Disable' : 'Enable'} relationship="label">
                          <Switch
                            checked={a.enabled}
                            onChange={(_, data) => handleToggleAgent(a.id, data.checked)}
                          />
                        </Tooltip>
                        <Tooltip content="Edit" relationship="label">
                          <Button icon={<EditRegular />} appearance="subtle" size="small" onClick={() => openEditAgentForm(a.id)} />
                        </Tooltip>
                        <Tooltip content="Delete" relationship="label">
                          <Button icon={<DeleteRegular />} appearance="subtle" size="small" onClick={() => setDeleteAgentTarget({ id: a.id, name: a.name })} />
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Model Dialogs ───────────────────────────────────────── */}
        <Dialog open={isFormOpen} onOpenChange={(_, data) => { if (!data.open) { setIsFormOpen(false); setEditingId(null); } }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{editingId ? 'Edit Model' : 'Add Model'}</DialogTitle>
              <DialogContent>
                <div className={styles.formGrid}>
                  <div>
                    <Label required>Name</Label>
                    <Input value={form.name} onChange={(_, d) => setForm(f => ({ ...f, name: d.value }))} placeholder="GPT-4o Mini" />
                  </div>
                  <div>
                    <Label required>Provider</Label>
                    <Dropdown
                      value={PROVIDERS.find(p => p.value === form.provider)?.label || ''}
                      selectedOptions={[form.provider]}
                      onOptionSelect={(_, d) => setForm(f => ({ ...f, provider: d.optionValue || 'azure-openai' }))}
                    >
                      {PROVIDERS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                    </Dropdown>
                  </div>
                  <div>
                    <Label required>Model ID</Label>
                    <Input value={form.model} onChange={(_, d) => setForm(f => ({ ...f, model: d.value }))} placeholder="gpt-4o-mini" />
                  </div>
                  <div>
                    <Label required>Endpoint</Label>
                    <Input value={form.endpoint} onChange={(_, d) => setForm(f => ({ ...f, endpoint: d.value }))} placeholder="https://your-resource.openai.azure.com" />
                  </div>
                  <div>
                    <Label required={!editingId}>API Key {editingId && '(leave blank to keep existing)'}</Label>
                    <Input type="password" value={form.apiKey} onChange={(_, d) => setForm(f => ({ ...f, apiKey: d.value }))} placeholder="sk-..." />
                  </div>
                  <div>
                    <Label>Capabilities</Label>
                    <Input value={form.capabilities} onChange={(_, d) => setForm(f => ({ ...f, capabilities: d.value }))} placeholder="chat, streaming, vision" />
                  </div>
                  <div className={styles.formFull}>
                    <Label>Description</Label>
                    <Input value={form.description} onChange={(_, d) => setForm(f => ({ ...f, description: d.value }))} placeholder="Brief description" />
                  </div>
                  <div>
                    <Label>Cost / 1K input tokens ($)</Label>
                    <Input type="number" value={form.costPer1kInputTokens} onChange={(_, d) => setForm(f => ({ ...f, costPer1kInputTokens: d.value }))} placeholder="0.00015" />
                  </div>
                  <div>
                    <Label>Cost / 1K output tokens ($)</Label>
                    <Input type="number" value={form.costPer1kOutputTokens} onChange={(_, d) => setForm(f => ({ ...f, costPer1kOutputTokens: d.value }))} placeholder="0.0006" />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
                <Button
                  icon={<SaveRegular />}
                  appearance="primary"
                  onClick={handleSave}
                  disabled={isSaving || !form.name || !form.model || !form.endpoint || (!editingId && !form.apiKey)}
                >
                  {isSaving ? <Spinner size="tiny" /> : 'Save'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {/* Delete model confirmation */}
        <Dialog open={!!deleteTarget} onOpenChange={(_, data) => { if (!data.open) setDeleteTarget(null); }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Delete Model</DialogTitle>
              <DialogContent>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
                <Button appearance="primary" onClick={handleDelete} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {/* ── Agent Dialogs ───────────────────────────────────────── */}
        <Dialog open={isAgentFormOpen} onOpenChange={(_, data) => { if (!data.open) { setIsAgentFormOpen(false); setEditingAgentId(null); } }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{editingAgentId ? 'Edit Foundry Agent' : 'Add Foundry Agent'}</DialogTitle>
              <DialogContent>
                <div className={styles.formGrid}>
                  <div>
                    <Label required>Name</Label>
                    <Input value={agentForm.name} onChange={(_, d) => setAgentForm(f => ({ ...f, name: d.value }))} placeholder="The DISP-atcher" />
                  </div>
                  <div>
                    <Label required>Foundry Agent ID</Label>
                    <Input value={agentForm.foundryAgentId} onChange={(_, d) => setAgentForm(f => ({ ...f, foundryAgentId: d.value }))} placeholder="asst_..." />
                  </div>
                  <div className={styles.formFull}>
                    <Label required>Foundry Endpoint</Label>
                    <Input value={agentForm.foundryEndpoint} onChange={(_, d) => setAgentForm(f => ({ ...f, foundryEndpoint: d.value }))} placeholder="https://resource.services.ai.azure.com/api/projects/project-name" />
                  </div>
                  <div className={styles.formFull}>
                    <Label required={!editingAgentId}>API Key {editingAgentId && '(leave blank to keep existing)'}</Label>
                    <Input type="password" value={agentForm.apiKey} onChange={(_, d) => setAgentForm(f => ({ ...f, apiKey: d.value }))} placeholder="Your Azure AI Foundry API key" />
                  </div>
                  <div className={styles.formFull}>
                    <Label>Description</Label>
                    <Input value={agentForm.description} onChange={(_, d) => setAgentForm(f => ({ ...f, description: d.value }))} placeholder="Brief description of what this agent does" />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
                <Button
                  icon={<SaveRegular />}
                  appearance="primary"
                  onClick={handleSaveAgent}
                  disabled={isSavingAgent || !agentForm.name || !agentForm.foundryAgentId || !agentForm.foundryEndpoint || (!editingAgentId && !agentForm.apiKey)}
                >
                  {isSavingAgent ? <Spinner size="tiny" /> : 'Save'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        {/* Delete agent confirmation */}
        <Dialog open={!!deleteAgentTarget} onOpenChange={(_, data) => { if (!data.open) setDeleteAgentTarget(null); }}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Delete Foundry Agent</DialogTitle>
              <DialogContent>
                Are you sure you want to delete <strong>{deleteAgentTarget?.name}</strong>? This cannot be undone.
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
                <Button appearance="primary" onClick={handleDeleteAgent} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
    </div>
  );
};
